// src/pages/employer/EmployerCandidates.jsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { jobAdAPI } from '../../services/api';
import { Spinner } from '../../components/shared/UI';
import toast from 'react-hot-toast';
import { ChevronDown, ChevronRight, Play } from 'lucide-react';

const BAND_COLOURS = {
  EXCELLENT: '#28A745',
  GOOD:      '#3D80CC',
  FAIR:      '#E6A817',
  POOR:      '#CC0000',
};

const ScorePill = ({ score, band }) => (
  <span style={{
    display: 'inline-block', padding: '3px 10px', borderRadius: 12,
    fontSize: 12, fontWeight: 700, color: '#fff',
    background: BAND_COLOURS[band] || '#999', minWidth: 44, textAlign: 'center',
  }}>
    {Math.round(score)}%
  </span>
);

const formatDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

/* ── Position accordion row ──────────────────────────────── */
const PositionRow = ({ position, models, onRunMatching, navigate }) => {
  const [open, setOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState('');
  const [running, setRunning] = useState(false);

  const posName = position.jobDefinition?.name || 'Position';
  const candidates = position.adMatches || [];
  const hasScores = candidates.some(c => c.computedAt && c.totalScore > 0);

  // Determine the last model used on this position's candidates
  const lastModelId = candidates.find(c => c.modelId)?.modelId || '';

  useEffect(() => {
    setSelectedModel(lastModelId);
  }, [lastModelId]);

  // Sort priority: match score (desc) → application date (asc).
  const sorted = [...candidates].sort((a, b) => {
    if (hasScores) return (b.totalScore || 0) - (a.totalScore || 0);
    return new Date(a.createdAt) - new Date(b.createdAt);
  });

  const handleRun = async () => {
    if (!selectedModel) { toast.error('Select a model first'); return; }
    setRunning(true);
    try {
      await onRunMatching(position.id, selectedModel);
      toast.success(`Matching complete — ${candidates.length} candidate${candidates.length !== 1 ? 's' : ''} scored`);
    } catch { /* interceptor toasts */ }
    finally { setRunning(false); }
  };

  return (
    <div style={{ border: '1px solid #ddd', borderRadius: 8, marginBottom: 10, background: '#fff' }}>
      {/* Title bar */}
      <div
        style={{
          padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
          cursor: 'pointer', background: open ? '#f0f6ff' : '#fafafa', borderRadius: open ? '8px 8px 0 0' : 8,
          borderBottom: open ? '1px solid #ddd' : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }} onClick={() => setOpen(o => !o)}>
          {open ? <ChevronDown size={16} color="#0e4e95" /> : <ChevronRight size={16} color="#666" />}
          <span style={{ fontSize: 14, fontWeight: 700, color: '#0e4e95' }}>{posName}</span>
          <span style={{ fontSize: 12, color: '#888', flexShrink: 0 }}>
            ({candidates.length} candidate{candidates.length !== 1 ? 's' : ''})
          </span>
        </div>

        {/* Model selector + Run button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          <select value={selectedModel} onChange={e => setSelectedModel(e.target.value)}
            style={{ fontSize: 12, padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', maxWidth: 200 }}>
            <option value="">— Select model —</option>
            {models.map(m => (
              <option key={m.id} value={m.id}>
                {m.modelName || 'Untitled'}{m.modelVersion ? ` v${m.modelVersion}` : ''}
              </option>
            ))}
          </select>
          <button onClick={handleRun} disabled={running || !selectedModel || candidates.length === 0}
            className="btn btn-primary btn-sm"
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', fontSize: 12 }}>
            {running ? <Spinner size={12} /> : <Play size={12} />}
            Run
          </button>
        </div>
      </div>

      {/* Candidates list */}
      {open && (
        <div style={{ padding: 0 }}>
          {sorted.length === 0 ? (
            <div style={{ padding: '20px 14px', color: '#888', fontSize: 13, textAlign: 'center' }}>
              No candidates have applied for this position yet.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                  <th style={{ padding: '8px 14px', fontWeight: 700, color: '#555' }}>Candidate</th>
                  <th style={{ padding: '8px 14px', fontWeight: 700, color: '#555' }}>Applied</th>
                  <th style={{ padding: '8px 14px', fontWeight: 700, color: '#555', textAlign: 'center' }}>Score</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(m => {
                  const s = m.jobSeeker || {};
                  return (
                    <tr key={m.id}
                      onClick={() => navigate(`/employer/candidates/reviews/${m.id}`)}
                      style={{ cursor: 'pointer', borderBottom: '1px solid #eee' }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f8f9fa'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                      <td style={{ padding: '8px 14px' }}>
                        <div style={{ fontWeight: 600, color: '#333' }}>{s.firstName} {s.lastName}</div>
                        {s.city && <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>{s.city}{s.country ? `, ${s.country}` : ''}</div>}
                      </td>
                      <td style={{ padding: '8px 14px', color: '#666' }}>{formatDate(m.createdAt)}</td>
                      <td style={{ padding: '8px 14px', textAlign: 'center' }}>
                        {m.totalScore > 0 || m.matchBand
                          ? <ScorePill score={m.totalScore} band={m.matchBand} />
                          : <span style={{ fontSize: 12, color: '#aaa' }}>n/a</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Main page ───────────────────────────────────────────── */
export const EmployerCandidates = () => {
  const { activeOrg, activeProfile } = useAuth();
  const [positions, setPositions] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    if (!activeOrg || !activeProfile) return;
    try {
      const [candRes, modelRes] = await Promise.all([
        jobAdAPI.getCandidates(activeOrg.id, activeProfile.id),
        jobAdAPI.getMatchingModels(activeOrg.id, activeProfile.id),
      ]);
      setPositions(candRes.data.data.positions || []);
      setModels((modelRes.data.data || []).sort((a, b) => {
        const nameA = (a.modelName || '').toLowerCase();
        const nameB = (b.modelName || '').toLowerCase();
        if (nameA !== nameB) return nameA.localeCompare(nameB);
        return (a.modelVersion || '').localeCompare(b.modelVersion || '');
      }));
    } catch { /* interceptor toasts */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [activeOrg, activeProfile]);

  const handleRunMatching = async (positionId, modelId) => {
    await jobAdAPI.runPositionMatching(activeOrg.id, activeProfile.id, positionId, { modelId });
    await load();
  };

  const totalCandidates = positions.reduce((sum, p) => sum + (p.adMatches?.length || 0), 0);

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1>Candidates</h1>
        <p>{positions.length} position{positions.length !== 1 ? 's' : ''} · {totalCandidates} candidate{totalCandidates !== 1 ? 's' : ''}</p>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner size={32} /></div>
      ) : positions.length === 0 ? (
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 4, padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
          No published positions found.
        </div>
      ) : (
        <div style={{ marginTop: 16 }}>
          {positions.map(pos => (
            <PositionRow key={pos.id} position={pos} models={models}
              onRunMatching={handleRunMatching} navigate={navigate} />
          ))}
        </div>
      )}
    </div>
  );
};
